import { useState, useMemo } from 'react';
import { Plus, Loader2, Receipt, Pencil, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/lib/error';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { SlideOver } from '@/components/ui/SlideOver';
import { AccessDenied } from '@/components/ui/AccessDenied';
import { useAuth } from '@/hooks/useAuth';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import {
  useChargeTypes,
  useCreateChargeType,
  useUpdateChargeType,
  useDeleteChargeType,
} from '@/hooks/api/useChargeTypes';
import { ChargeTypeCategory, UserRole, type ChargeType } from '@pms/shared';

// ─── Constants ─────────────────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<ChargeTypeCategory, string> = {
  [ChargeTypeCategory.ROOM]: 'Room',
  [ChargeTypeCategory.FOOD]: 'Food',
  [ChargeTypeCategory.TAX]: 'Tax',
  [ChargeTypeCategory.SERVICE]: 'Service',
  [ChargeTypeCategory.MISC]: 'Misc',
};

const CATEGORY_OPTIONS = Object.values(ChargeTypeCategory);

function fmtMoney(amount: string | null): string {
  if (amount === null) return '--';
  const n = parseFloat(amount);
  if (Number.isNaN(n)) return '--';
  return n.toFixed(2);
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function ChargeTypesPage() {
  const { user } = useAuth();
  const canAccess = user?.role === UserRole.ADMIN;
  const propertyId = useSelectedPropertyId();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<ChargeType | null>(null);
  const [search, setSearch] = useState('');

  const listQuery = useChargeTypes({ propertyId }, !!propertyId && canAccess);
  const deleteMutation = useDeleteChargeType();

  const allItems: ChargeType[] = listQuery.data?.data ?? [];

  const items: ChargeType[] = useMemo(() => {
    if (!search.trim()) return allItems;
    const q = search.toLowerCase();
    return allItems.filter(
      (c) => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q),
    );
  }, [allItems, search]);

  function handleDelete(item: ChargeType) {
    if (!window.confirm('Delete this charge type?')) return;
    deleteMutation.mutate(item.id, {
      onError: (err) => {
        // Server soft-blocks when referenced by folio lines (409 IN_USE).
        window.alert(extractErrorMessage(err));
      },
    });
  }

  if (!canAccess) {
    return <AccessDenied message="Charge type configuration is restricted to Admins." />;
  }

  return (
    <div>
      <PageHeader
        title="Charge Types"
        description="Folio charge categories and posting codes"
        actions={
          <Button
            onClick={() => { setEditing(null); setShowForm(true); }}
            disabled={!propertyId}
          >
            <Plus size={16} aria-hidden="true" />
            Add Charge Type
          </Button>
        }
      />

      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by name or code..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-64"
          aria-label="Search charge types"
        />
      </div>

      {!propertyId ? (
        <EmptyState icon={Receipt} title="Select a property" description="Select a property to continue." />
      ) : listQuery.isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-primary-500" aria-label="Loading" />
        </div>
      ) : listQuery.isError ? (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(listQuery.error)}
        </p>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Receipt}
          title="No charge types"
          description="Create your first charge type."
          actionLabel="Add Charge Type"
          onAction={() => setShowForm(true)}
        />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Code</Th>
                  <Th>Category</Th>
                  <Th>Default Amount</Th>
                  <Th>Taxable</Th>
                  <Th>Active</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{c.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 font-mono">{c.code}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{CATEGORY_LABELS[c.category]}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{fmtMoney(c.defaultAmount)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{c.taxable ? 'Yes' : 'No'}</td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                          c.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500',
                        )}
                      >
                        {c.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => { setEditing(c); setShowForm(true); }} className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50" aria-label="Edit">
                          <Pencil size={14} />
                        </button>
                        <button type="button" onClick={() => handleDelete(c)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" aria-label="Delete">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <SlideOver
        isOpen={showForm}
        onClose={() => { setShowForm(false); setEditing(null); }}
        title={editing ? 'Edit Charge Type' : 'Add Charge Type'}
        width="max-w-xl"
      >
        <ChargeTypeForm
          propertyId={propertyId}
          editing={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
        />
      </SlideOver>
    </div>
  );
}

// ─── Form ────────────────────────────────────────────────────────────────────

function ChargeTypeForm({
  propertyId,
  editing,
  onClose,
}: {
  propertyId: string;
  editing: ChargeType | null;
  onClose: () => void;
}) {
  const [name, setName] = useState(editing?.name ?? '');
  const [code, setCode] = useState(editing?.code ?? '');
  const [category, setCategory] = useState<ChargeTypeCategory>(
    editing?.category ?? ChargeTypeCategory.MISC,
  );
  const [defaultAmount, setDefaultAmount] = useState(
    editing?.defaultAmount != null ? parseFloat(editing.defaultAmount).toFixed(2) : '',
  );
  const [taxable, setTaxable] = useState(editing?.taxable ?? false);
  const [postingCode, setPostingCode] = useState(editing?.postingCode ?? '');
  const [isActive, setIsActive] = useState(editing?.isActive ?? true);
  const [amountError, setAmountError] = useState('');

  const createMutation = useCreateChargeType();
  const updateMutation = useUpdateChargeType();
  const mutationError = createMutation.error ?? updateMutation.error;

  const isValid = name.trim().length > 0 && code.trim().length > 0;

  function clearError() {
    if (createMutation.isError) createMutation.reset();
    if (updateMutation.isError) updateMutation.reset();
  }

  function normalizeAmountOnBlur() {
    if (!defaultAmount.trim()) return;
    const n = parseFloat(defaultAmount);
    if (Number.isNaN(n) || n < 0) {
      setAmountError('Enter a valid amount of 0 or more.');
      return;
    }
    setAmountError('');
    setDefaultAmount(n.toFixed(2));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid) return;

    let parsedAmount: number | undefined;
    if (defaultAmount.trim()) {
      const n = parseFloat(defaultAmount);
      if (Number.isNaN(n) || n < 0) {
        setAmountError('Enter a valid amount of 0 or more.');
        return;
      }
      parsedAmount = Number(n.toFixed(2));
    }

    if (editing) {
      updateMutation.mutate(
        {
          id: editing.id,
          payload: {
            name: name.trim(),
            code: code.trim(),
            category,
            defaultAmount: parsedAmount,
            taxable,
            postingCode: postingCode.trim() || undefined,
            isActive,
          },
        },
        { onSuccess: () => onClose() },
      );
    } else {
      createMutation.mutate(
        {
          propertyId,
          name: name.trim(),
          code: code.trim(),
          category,
          defaultAmount: parsedAmount,
          taxable,
          postingCode: postingCode.trim() || undefined,
          isActive,
        },
        { onSuccess: () => onClose() },
      );
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="ct-name" className="block text-sm font-medium text-gray-700 mb-1">
          Name <span className="text-red-500">*</span>
        </label>
        <input id="ct-name" type="text" required value={name} onChange={(e) => { setName(e.target.value); clearError(); }} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>
      <div>
        <label htmlFor="ct-code" className="block text-sm font-medium text-gray-700 mb-1">
          Code <span className="text-red-500">*</span>
        </label>
        <input id="ct-code" type="text" required maxLength={100} value={code} onChange={(e) => { setCode(e.target.value); clearError(); }} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500" />
        {editing && (
          <p className="mt-1 text-xs text-gray-400">Editing the code does not rewrite historical folio postings.</p>
        )}
      </div>
      <div>
        <label htmlFor="ct-category" className="block text-sm font-medium text-gray-700 mb-1">
          Category <span className="text-red-500">*</span>
        </label>
        <select id="ct-category" value={category} onChange={(e) => setCategory(e.target.value as ChargeTypeCategory)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500">
          {CATEGORY_OPTIONS.map((c) => (
            <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor="ct-amount" className="block text-sm font-medium text-gray-700 mb-1">Default Amount</label>
        <input id="ct-amount" type="number" min="0" step="0.01" value={defaultAmount} onChange={(e) => { setDefaultAmount(e.target.value); setAmountError(''); }} onBlur={normalizeAmountOnBlur} placeholder="0.00" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
        {amountError && <p className="mt-1 text-xs text-red-600">{amountError}</p>}
      </div>
      <div>
        <label htmlFor="ct-posting" className="block text-sm font-medium text-gray-700 mb-1">GL / Posting Code</label>
        <input id="ct-posting" type="text" maxLength={100} value={postingCode} onChange={(e) => setPostingCode(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>
      <div className="flex items-center gap-2">
        <input id="ct-taxable" type="checkbox" checked={taxable} onChange={(e) => setTaxable(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
        <label htmlFor="ct-taxable" className="text-sm font-medium text-gray-700">Taxable</label>
      </div>
      <div className="flex items-center gap-2">
        <input id="ct-active" type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
        <label htmlFor="ct-active" className="text-sm font-medium text-gray-700">Active</label>
      </div>

      {mutationError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(mutationError)}
        </p>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
        <Button type="submit" isLoading={createMutation.isPending || updateMutation.isPending} disabled={!isValid}>
          {editing ? 'Update' : 'Create'}
        </Button>
      </div>
    </form>
  );
}

// ─── Shared ──────────────────────────────────────────────────────────────────

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
      {children}
    </th>
  );
}
